import React, { useEffect, useRef, useState } from 'react';
import { usePayPalScriptReducer } from '@paypal/react-paypal-js';

// Apple Pay yalnızca Apple cihazlarda Safari'de + doğrulanmış domainde çalışır.
// localhost'ta config.isEligible false döner → düğme görünmez. Canlıda
// /.well-known/apple-developer-merchantid-domain-association + PayPal panelinde
// domain kaydı gerekir.

let applePaySdkPromise;
const loadApplePaySdk = () => {
  if (window.ApplePaySession && document.querySelector('apple-pay-button')) {
    return Promise.resolve();
  }
  if (applePaySdkPromise) return applePaySdkPromise;
  applePaySdkPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.onload = resolve;
    s.onerror = resolve; // <apple-pay-button> öğesi olmasa da session ile devam
    document.head.appendChild(s);
  });
  return applePaySdkPromise;
};

const ApplePayButton = (props) => {
  const [{ isResolved }] = usePayPalScriptReducer();
  const propsRef = useRef(props);
  propsRef.current = props;
  const configRef = useRef(null);
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    if (!isResolved) return undefined;
    if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments?.()) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await loadApplePaySdk();
        const ap = window.paypal?.Applepay?.();
        if (!ap) return;
        const config = await ap.config();
        if (cancelled || !config?.isEligible) return;
        configRef.current = config;
        setEligible(true);
      } catch (err) {
        // Cihaz/tarayıcı uygunluk tespiti: Apple olmayan ortamlarda başarısız
        // olması BEKLENEN durumdur (düğme gizli kalır). Kullanıcıya gösterilmez;
        // hata ayıklama için yalnızca debug seviyesinde loglanır.
        console.debug('Apple Pay uygun değil / yüklenemedi:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isResolved]);

  const onClick = () => {
    const p = propsRef.current;
    const config = configRef.current;
    if (!config || p.disabled || !p.amount) return;
    const ap = window.paypal.Applepay();

    const paymentRequest = {
      countryCode: config.countryCode,
      currencyCode: p.currency,
      merchantCapabilities: config.merchantCapabilities,
      supportedNetworks: config.supportedNetworks,
      requiredBillingContactFields: ['name', 'postalAddress'],
      total: { label: p.label || 'Utkan eSIM', type: 'final', amount: String(p.amount) },
    };

    let session;
    try {
      session = new window.ApplePaySession(4, paymentRequest);
    } catch (err) {
      p.onError?.(err.message);
      return;
    }

    session.onvalidatemerchant = (event) => {
      ap
        .validateMerchant({ validationUrl: event.validationURL, displayName: 'Utkan eSIM' })
        .then((r) => session.completeMerchantValidation(r.merchantSession))
        .catch((err) => {
          session.abort();
          p.onError?.(err.message);
        });
    };

    session.onpaymentauthorized = async (event) => {
      try {
        const { orderId } = await p.createOrder();
        await ap.confirmOrder({
          orderId,
          token: event.payment.token,
          billingContact: event.payment.billingContact,
        });
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        await p.captureAndFinalize(orderId);
      } catch (err) {
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        p.onError?.(err.message);
      }
    };

    session.begin();
  };

  if (!eligible) return null;

  return (
    <apple-pay-button
      buttonstyle="black"
      type="buy"
      locale="en-US"
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        marginBottom: '10px',
        cursor: 'pointer',
        '--apple-pay-button-width': '100%',
        '--apple-pay-button-height': '48px',
        '--apple-pay-button-border-radius': '10px',
      }}
    />
  );
};

export default ApplePayButton;
